import { notificationService } from "../services/api";

const DEVICE_NAME_COOKIE = "device_name";
const DEVICE_NAME_MAX_AGE = 60 * 60 * 24 * 365;

export function generateDeviceName(seed) {
  return `${seed}-${new Date().toISOString()}`;
}

export function setDeviceName(value) {
  document.cookie = `${DEVICE_NAME_COOKIE}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${DEVICE_NAME_MAX_AGE}`;
}

export function getDeviceName() {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${DEVICE_NAME_COOKIE}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(DEVICE_NAME_COOKIE.length + 1));
}

export function getOrCreateDeviceName(seed) {
  const existing = getDeviceName();
  if (existing) return existing;
  const deviceName = generateDeviceName(seed);
  setDeviceName(deviceName);
  return deviceName;
}

export function clearDeviceName() {
  document.cookie = `${DEVICE_NAME_COOKIE}=; path=/; max-age=0`;
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push notifications are not supported in this browser");
  }
  const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
  // Reuse the registration created by serviceWorkerRegistration.register()
  // so a single registration carries the update handlers.
  const existing = await navigator.serviceWorker.getRegistration(swUrl);
  if (existing) return existing;
  return navigator.serviceWorker.register(swUrl);
}

function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return window
    .btoa(str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Get user subscription for push notifications, or subscribe if not already subscribed.
 * Re-subscribes if the existing subscription was created with a different
 * VAPID key than the one the server currently uses.
 * @param {ServiceWorkerRegistration} registration
 * @returns {PushSubscription}
 */
export async function subscribeUserToPush(registration) {
  const { data } = await notificationService.getPublicKey();
  const serverKey = data.public_key.replace(/=+$/, "");
  const existingSubscription = await registration.pushManager.getSubscription();

  if (existingSubscription) {
    const existingKey = existingSubscription.options?.applicationServerKey
      ? bufferToBase64Url(existingSubscription.options.applicationServerKey)
      : null;
    if (existingKey === serverKey) return existingSubscription;
    await existingSubscription.unsubscribe();
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });
}

export async function sendSubscriptionToServer(
  uuids,
  subscription,
  cookieDeviceName,
) {
  const body = {
    collaborator_uuid: uuids.collaborator_uuid,
    business_uuid: uuids.business_uuid,
    customer_uuid: uuids.customer_uuid,
    subscription,
  };
  const { status, data } = await notificationService.postSubscription(
    body,
    cookieDeviceName,
  );
  if (status !== 200)
    throw new Error(`Failed to send subscription to server. Status: ${status}`);
  return { deviceName: cookieDeviceName, response: data };
}
